import { Entity, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Column } from '../../decorators';
import { CatalogType } from '../../enums/catalog-type.enum';
import { BaseEntity } from './base.entity';

/**
 * A single value in a managed lookup catalog (PD-14226, Option A).
 *
 * One generic table backs every consolidated catalog — merchandiser
 * manufacturer/model and any future lookup (see CATALOG_TYPE_REGISTRY).
 * `catalogType` discriminates
 * which catalog a row belongs to. `parentId` links a child-level value
 * (e.g. a model) to its parent (e.g. a manufacturer) for hierarchical
 * catalogs; it is null for flat catalogs and top-level values.
 *
 * Consuming entities reference a value via a generic
 * `fk_catalog_value__id_*` column (e.g. merchandiser.manufacturerId) rather
 * than a per-domain FK — that genericity is the whole point of the
 * single-table design.
 */
@Index('catalog_value_type_idx', ['catalogType'])
@Index('catalog_value_type_parent_idx', ['catalogType', 'parentId'])
@Entity('catalog_value')
export class CatalogValueEntity extends BaseEntity {
	/** Which catalog this value belongs to (registry discriminator). */
	@Column('varchar', { name: 'catalogType', length: 64 })
	public catalogType!: CatalogType;

	/** Display value, e.g. "True Manufacturing", "Convenience Store". */
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	/**
	 * Parent value for hierarchical catalogs (a model's manufacturer, a
	 * subcategory's category). Null for flat catalogs / top-level values.
	 */
	@Column('int', {
		name: 'fk_catalog_value__id_parent',
		unsigned: true,
		nullable: true,
	})
	public parentId!: number | null;

	@ManyToOne(() => CatalogValueEntity, (cv) => cv.children, {
		nullable: true,
		onDelete: 'SET NULL',
	})
	@JoinColumn({ name: 'fk_catalog_value__id_parent' })
	public parent!: CatalogValueEntity | null;

	@OneToMany(() => CatalogValueEntity, (cv) => cv.parent)
	public children!: Array<CatalogValueEntity>;

	@Column({ type: 'boolean', name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@Column('int', { name: 'sortOrder', default: () => "'0'" })
	public sortOrder!: number;

	/**
	 * PD-17109 — for catalogs whose registry entry lists `outcomes` (the job
	 * competitor status list), the fixed outcome this
	 * value means ("Supplying" → supplying), so code keys off the outcome rather
	 * than a name the tenant may rename. Null for every other catalog.
	 * Nullable and new, so existing rows and SM1 (which does not read
	 * catalog_value) are unaffected.
	 */
	@Column('varchar', { name: 'outcome', length: 16, nullable: true })
	public outcome!: string | null;
}
